import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCheckbox,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonLoading,
  IonRow,
  IonTextarea,
  useIonAlert,
} from "@ionic/react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Bin, MessageResult, POReceiptData } from "../models/purchase-order";
import { chevronDownOutline, chevronUpOutline } from "ionicons/icons";
import {
  CreatePOReceiptsRequestObject,
  validatePOReceiptData,
} from "../api/PurchaseOrderLines";
import { getCreatePOReceiptsPayload } from "../util/PoHelper";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";

type Props = {
  line: POReceiptData;
  billOfLading: string;
  employee: string;
  onPoUpdate: (poData: CreatePOReceiptsRequestObject) => void;
  hasPendingUpdates?: boolean;
};

const FIELDS = [
  { label: "Line", field: "Line" },
  { label: "Item", field: "Item" },
  { label: "Item Description", field: "ItemDescription" },
  { label: "Stores Location", field: "StoresLocation" },
];

const POLine: React.FC<Props> = ({
  line,
  billOfLading,
  employee,
  hasPendingUpdates,
  onPoUpdate,
}) => {
  const {
    QtyReceived,
    ReceivedComplete,
    Result,
    SupplierReturnFlag,
    Comments,
    Bin,
    CfRemainingOnOrder,
  } = line;
  const [showLoading, setShowLoading] = useState(false);
  const [validationResult, setValidationResult] = useState<MessageResult>();
  const [isExpanded, setIsExpanded] = useState(false);
  const [comments, setComments] = useState(Comments);
  const [receivedComplete, setReceivedComplete] = useState(ReceivedComplete);
  const [returnToSupplier, setReturnToSupplier] = useState(SupplierReturnFlag);
  const [askConfirmation] = useIonAlert();
  const { translations } = useContext(TranslationsContext);
  const [binQuantity, setBinQuantity] = useState<{
    [objectId: string]: number;
  }>({});
  const { translatedMessages } = useContext(TranslationMessagesContext);

  const checkboxRef = useRef<HTMLIonCheckboxElement>(null);

  const isSuccessful = Result && Result.Success;
  const hasErrors =
    (Result && !Result.Success) ||
    (validationResult && !validationResult.Success);
  // const state = ReceivedComplete ? "Received Complete" : "Partially Received";
  let cardClasses = "";
  if (ReceivedComplete || isSuccessful)
    cardClasses = `${cardClasses} card-highlight-success`;
  else if (hasErrors) cardClasses = `${cardClasses} card-highlight-failure`;
  const errorMessages =
    [
      ...(Result?.MessageList ?? []),
      ...(validationResult?.MessageList ?? []),
    ].map((m) => m.Text) ?? [];

  const qtyRemaining = ReceivedComplete ? 0 : CfRemainingOnOrder;
  const primaryBin = Bin?.find((b) => b.BinType === "Primary");
  const hasBin = !!Bin;
  const quantityReceived = Object.values(binQuantity).reduce(
    (total, curr) => (total += curr),
    0
  );

  const updatePO = useCallback(
    (bin?: Bin, value?: number) => {
      onPoUpdate(
        getCreatePOReceiptsPayload({
          billOfLading,
          comments,
          data: line,
          employee,
          receivedComplete,
          supplierReturnFlag: returnToSupplier,
          transactionQuantity: value ?? 0,
          bin: bin?.Bin,
        })
      );
    },
    [
      billOfLading,
      comments,
      employee,
      line,
      onPoUpdate,
      receivedComplete,
      returnToSupplier,
    ]
  );

  const handlePOUpdate = useCallback(() => {
    const isReceivedCompleteChanged = receivedComplete !== ReceivedComplete;

    if (isReceivedCompleteChanged && Object.keys(binQuantity).length === 0) {
      updatePO();
    } else {
      //update PO for all the bin
      for (const [key, value] of Object.entries(binQuantity)) {
        const bin = Bin?.find((b) => b.ObjectId === key);
        updatePO(bin, value);
      }
    }
  }, [Bin, ReceivedComplete, binQuantity, receivedComplete, updatePO]);

  //reset the bin quantity if the receive is successful
  useEffect(() => {
    if (isSuccessful && !hasPendingUpdates) setBinQuantity({});
  }, [hasPendingUpdates, isSuccessful]);

  //keep a watch on changes
  useEffect(() => {
    if (
      receivedComplete !== ReceivedComplete ||
      returnToSupplier !== SupplierReturnFlag
    )
      handlePOUpdate();
  }, [
    ReceivedComplete,
    SupplierReturnFlag,
    handlePOUpdate,
    receivedComplete,
    returnToSupplier,
  ]);

  const handleBlur = useCallback(async () => {
    const hasQuantityReceivedChange = QtyReceived !== quantityReceived;
    if (!hasQuantityReceivedChange) return; //nothing changed
    setShowLoading(true);
    let bin = [...(line.Bin ?? [])];
    if (bin.length > 0)
      bin = bin.map((b) => {
        const updateQty = binQuantity[b.ObjectId];
        if (typeof updateQty === "number") {
          return { ...b, QtyReceived: updateQty };
        }
        return b;
      });
    const response = await validatePOReceiptData({
      Confirmation: {},
      RequestObject: {
        ...line,
        QtyReceived: quantityReceived,
        Bin: bin.length > 0 ? bin : null,
      },
    });
    const data: MessageResult = response.data;
    const qtyReceivedMore = data.MessageList.find(
      (m) => m.Id === "QtyRecMoreQtyOrderW"
    );
    if (qtyReceivedMore) {
      askConfirmation({
        header: qtyReceivedMore.Text,
        backdropDismiss: false,
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
          },
          {
            text: translations["lbl_btn_cancel"] || "Cancel",
            role: "cancel",
            handler: () => {
              setBinQuantity({});
            },
          },
        ],
      });
    }
    if (data.MessageList) setValidationResult(data);
    setShowLoading(false);

    if (data.Success) handlePOUpdate();
  }, [
    QtyReceived,
    askConfirmation,
    binQuantity,
    handlePOUpdate,
    line,
    quantityReceived,
    translations,
  ]);

  const toggleWithConfirmation = useCallback(
    (checked: boolean, message: string) => {
      askConfirmation({
        message,
        buttons: [
          {
            text: translations["lbl_btn_no"] || "No",
            role: "cancel",
            handler: () => {
              requestAnimationFrame(() => {
                if (checkboxRef.current !== null) {
                  checkboxRef.current.checked = !checked;
                }
              });
            },
          },
          {
            text: translations["lbl_btn_yes"] || "Yes",
            role: "confirm",
            handler: () => {
              setReceivedComplete(checked);
            },
          },
        ],
      });
    },
    [askConfirmation, translations]
  );

  return (
    <>
      <IonLoading isOpen={showLoading} />
      <IonCard className={cardClasses}>
        <IonCardContent>
          <IonGrid class="ion-no-padding no-padding-top">
            <IonRow>
              <IonCol>
                {hasErrors &&
                  errorMessages.length > 0 &&
                  errorMessages.map((e) => (
                    <p key={e} className="error-message">
                      {e}
                    </p>
                  ))}
              </IonCol>
              <IonCol size="auto" class="ion-no-padding">
                <IonButton
                  size="small"
                  fill="clear"
                  onMouseDown={(e) => {
                    e.preventDefault(); // This will prevent the onBlur event from firing
                    setIsExpanded((pre) => !pre);
                  }}
                >
                  <IonIcon
                    size="small"
                    slot="icon-only"
                    icon={isExpanded ? chevronUpOutline : chevronDownOutline}
                  ></IonIcon>
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow class="ion-padding-top">
              <IonCol>
                {FIELDS.map((field) => {
                  const data = line[field.field as keyof POReceiptData];
                  return (
                    <p key={field.field}>
                      {field.label}: {data}
                    </p>
                  );
                })}
                <p>Qty Remaining: {qtyRemaining}</p>
                {isExpanded && (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        marginBottom: "4px",
                        marginTop: "4px",
                      }}
                    >
                      <IonCheckbox
                        checked={receivedComplete}
                        ref={checkboxRef}
                        onIonChange={(event: CustomEvent) => {
                          const { checked } = event.detail;

                          if (qtyRemaining > 0 && checked) {
                            toggleWithConfirmation(
                              checked,
                              translatedMessages["MobPOMarkedComplete"]
                                ?.MessageText ||
                                "Warning - Quantity Remaining is not zero, but the line has been marked Complete. Continue?"
                            );
                          } else if (qtyRemaining === 0 && !checked) {
                            toggleWithConfirmation(
                              checked,
                              translatedMessages["MobPONotMarkedComplete"]
                                ?.MessageText ||
                                "Warning - Quantity Remaining is zero, but the line has not been marked Complete. Continue?"
                            );
                          } else return setReceivedComplete(checked);
                        }}
                      ></IonCheckbox>
                      <IonLabel>Received Complete</IonLabel>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        marginTop: "6px",
                      }}
                    >
                      <IonCheckbox
                        checked={returnToSupplier}
                        onClick={() => {
                          setReturnToSupplier((pre) => !pre);
                        }}
                      ></IonCheckbox>
                      <IonLabel>Return To Supplier</IonLabel>
                    </div>
                  </>
                )}
              </IonCol>
              <IonCol size="auto">
                <IonItem
                  fill="outline"
                  mode="md"
                  style={{ maxWidth: 84 }}
                  lines="full"
                >
                  <IonInput
                    value={quantityReceived}
                    onIonInput={async (ev) => {
                      const { value } = ev.target;
                      //assign everything into primary bin
                      if (primaryBin)
                        setBinQuantity({
                          [primaryBin.ObjectId]: value ? Number(value) : 0,
                        });
                      else if (!hasBin) {
                        //in case of ECatalog
                        setBinQuantity({
                          ECatalog: value ? Number(value) : 0,
                        });
                      }
                    }}
                    onBlur={handleBlur}
                    type="number"
                  ></IonInput>
                </IonItem>
              </IonCol>
            </IonRow>
            {isExpanded && (
              <>
                {(Bin ?? []).map((b) => (
                  <IonRow class="ion-padding-top">
                    <IonCol style={{ display: "flex", alignItems: "center" }}>
                      {b.Bin}
                    </IonCol>
                    <IonCol size="auto">
                      <IonItem
                        fill="outline"
                        mode="md"
                        style={{ maxWidth: 84 }}
                        lines="full"
                      >
                        <IonInput
                          value={binQuantity[b.ObjectId] ?? 0}
                          onIonInput={async (ev) => {
                            const { value } = ev.target;
                            setBinQuantity((pre) => ({
                              ...pre,
                              [b.ObjectId]: value ? Number(value) : 0,
                            }));
                          }}
                          onBlur={handleBlur}
                          type="number"
                        ></IonInput>
                      </IonItem>
                    </IonCol>
                  </IonRow>
                ))}
                <IonRow class="ion-padding-top">
                  <IonCol style={{ display: "flex", alignItems: "center" }}>
                    <IonLabel>
                      {translations["lbl_category_comments"] || "Comments"}:
                    </IonLabel>
                  </IonCol>
                  <IonCol>
                    <IonTextarea
                      class="wo-editable-input"
                      autoGrow
                      value={comments}
                      onIonChange={(ev: any) => {
                        setComments(ev.detail.value);
                      }}
                    ></IonTextarea>
                  </IonCol>
                </IonRow>
              </>
            )}
          </IonGrid>
        </IonCardContent>
      </IonCard>
    </>
  );
};

export default POLine;
